import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  type IconName,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  toastManager,
} from "@salestrak/ui";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useUpdateAgeDialog, useFormatAgeDate } from "#app/lib/hooks/use-update-age-dialog";
import { AGE_STATUS, AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { Omittable } from "@salestrak/ui";

// DialogPopup has no size prop, so the width is authored.

// Keyed by the status union, not by `string`: a total record reads back an
// `IconName`, and adding a status makes tsc demand its icon here.
const AGE_STATUS_ICONS: Record<AgeStatus, IconName> = {
  onTrack: "check-circle",
  atRisk: "alert-triangle",
  offTrack: "alert-circle",
  disabled: "x-circle",
};

// `background` is the status' semantic tone name and is exactly the
// success/warning/destructive/muted union Icon speaks. The old
// `iconClassName: text-${colorToken}` template class could never be scanned
// by Tailwind anyway.
// Driven by `AGE_STATUS` rather than `Object.entries`, whose keys widen to
// `string` and turn both total records back into partial lookups.
const AGE_DIALOG_OPTIONS = Object.values(AGE_STATUS).map((status) => ({
  value: status,
  label: AGE_STATUS_CONFIG[status].label,
  icon: AGE_STATUS_ICONS[status],
  tone: AGE_STATUS_CONFIG[status].background,
}));

const NO_REASON_VALUE = "__none__";

export type UpdateAgeDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  stageId?: string | null;
  age?: {
    date?: string | null;
    status?: string | null;
    reasonId?: string | null;
  } | null;
}>;

export function UpdateAgeDialog({
  open,
  onOpenChange,
  issueId,
  stageId,
  age,
}: UpdateAgeDialogProps) {
  const { stageMap } = useWorkspaceData();
  const manualUpdatesDisabled =
    !!stageId && stageMap.get(stageId)?.age?.allowManualUpdates === false;

  const { settings, reasons, defaultReason, updateAge } = useUpdateAgeDialog(
    open && !manualUpdatesDisabled,
  );
  const updatedAt = useFormatAgeDate(age?.date);

  const [selectedStatus, setSelectedStatus] = useState<AgeStatus>(
    AGE_STATUS.onTrack,
  );
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const editorKeyRef = useRef(0);

  // Reset state only on open transition (not when data refreshes mid-edit)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedStatus(AGE_STATUS.onTrack);
      setSelectedReasonId(
        age?.reasonId ?? defaultReason?.reasonId ?? null,
      );
      setNote("");
      setSubmitting(false);
      editorKeyRef.current += 1;
    }
    prevOpenRef.current = open;
  }, [open, age?.reasonId, defaultReason?.reasonId]);

  const currentStatusOption = useMemo(
    () => AGE_DIALOG_OPTIONS.find((o) => o.value === selectedStatus),
    [selectedStatus],
  );

  const reasonLabel = useMemo(() => {
    if (!selectedReasonId) return "No reason";
    return reasons.find((r) => r.reasonId === selectedReasonId)?.name ?? "No reason";
  }, [selectedReasonId, reasons]);

  const canSubmit = useMemo(() => {
    if (
      settings.reasonsEnabled &&
      settings.requireReason &&
      !selectedReasonId
    ) {
      return false;
    }
    if (settings.requireComment && !note.replace(/<[^>]*>/g, "").trim()) {
      return false;
    }
    return true;
  }, [settings, selectedReasonId, note]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await updateAge({
        issueId,
        status: selectedStatus,
        note,
        reasonId: selectedReasonId,
        reasonsEnabled: settings.reasonsEnabled,
      });
      toastManager.add({ title: "Age updated", type: "success" });
      onOpenChange(false);
    } catch (err) {
      toastMutationError(err, "Failed to update age");
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    submitting,
    updateAge,
    issueId,
    selectedStatus,
    note,
    settings.reasonsEnabled,
    selectedReasonId,
    onOpenChange,
  ]);

  if (manualUpdatesDisabled) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Manual age updates are disabled</AlertDialogTitle>
            <AlertDialogDescription>
              Manual age updates are disabled for this stage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Close
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup size="2xl" data-testid="update-age-dialog">
        <DialogHeader>
          <Box direction="row" align="center" gap="sm">
            <Icon name="clock" size="lg" color="muted" />
            <DialogTitle>Update age</DialogTitle>
          </Box>
        </DialogHeader>
        <DialogPanel>
          <Box direction="column" gap="sm">
            <Box direction="row" gap="sm" wrap>
              <Select
                value={selectedStatus}
                onValueChange={(value) => {
                  if (typeof value === "string") {
                    setSelectedStatus(value as AgeStatus);
                  }
                }}
              >
                <SelectTrigger size="sm" width="auto">
                  <SelectValue placeholder="Select a status">
                    {() =>
                      currentStatusOption ? (
                        <Box direction="row" align="center" gap="xs">
                          <Icon
                            name={currentStatusOption.icon}
                            size="sm"
                            color={currentStatusOption.tone}
                          />
                          {currentStatusOption.label}
                        </Box>
                      ) : null
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup>
                  {AGE_DIALOG_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <Box direction="row" align="center" gap="xs">
                        <Icon name={option.icon} size="sm" color={option.tone} />
                        {option.label}
                      </Box>
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>

              {settings.reasonsEnabled && (
                <Select
                  value={selectedReasonId ?? NO_REASON_VALUE}
                  onValueChange={(value) => {
                    setSelectedReasonId(
                      typeof value === "string" && value !== NO_REASON_VALUE
                        ? value
                        : null,
                    );
                  }}
                >
                  <SelectTrigger size="sm" width="auto">
                    <SelectValue placeholder="No reason">
                      {() => reasonLabel}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectPopup>
                    {!settings.requireReason && (
                      <SelectItem value={NO_REASON_VALUE}>
                        <Text size="sm" variant="muted">
                          No reason
                        </Text>
                      </SelectItem>
                    )}
                    {reasons.map((reason) => (
                      <SelectItem key={reason.reasonId} value={reason.reasonId}>
                        <Text size="sm">{reason.name}</Text>
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              )}
            </Box>

            <Box border rounded="lg" overflow="hidden" data-testid="update-age-note">
              <RichTextEditor
                key={editorKeyRef.current}
                content=""
                onUpdate={setNote}
                placeholder="Write a comment"
                shortcutHints
                mentions
                compact
                contentPaddingX="sm"
                contentPaddingY="sm"
              />
            </Box>

            {updatedAt ? (
              <Text size="xs" variant="muted">
                {updatedAt}
              </Text>
            ) : null}
          </Box>
        </DialogPanel>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            Post update
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
